import { getSelectedCartItems } from '../data/data-provider';
import { ICartData } from '../interfaces';
import {
    ConfirmationMsgVisibleReferer,
    IConfirmationMsgVisibleResult,
    isCampaignDataValid,
} from './free-plugin-provider';
import { FreePluginEnum } from '../enums/free-plugins-enum';
import { ProductDepartmentEnum } from '../../../products/enums/product-department-enum';
import { round2DecimalPlaces } from '../../../../domain/math-provider';

// https://wavesaudio.atlassian.net/browse/DSWEB-5475

export interface IEfsMilestone {
    amount: number; // amount in $
    displayAmount: number;
    percent: number;
    isChecked: boolean;
}

export interface IEfsCheckedItem {
    receiveCount: number;
    missingAmount: number;
    percent: number;
}

export interface IFreePluginProgressBarWithMilestones {
    enabled: boolean;
    min?: number;
    max?: number;
    milestones?: IEfsMilestone[];
    checkedItem?: IEfsCheckedItem|null;
}

/**
 * Round to the nearest 10
 */
const getDisplayAmount = (amount: number): number => {
    return Math.round(amount / 10) * 10;
};

export const initFreePluginProgressBarWithMilestones = (cartData: ICartData) : IFreePluginProgressBarWithMilestones => {

    try{
        if(!isCampaignDataValid(cartData)) {
            return {
                enabled: false,
            };
        }

        const campaignID = Number(cartData.efsCampaignTypeID) || 0;
        if(campaignID !== FreePluginEnum.TotalTiers) {
            return {
                enabled: false,
            };
        }

        const efsData = [...cartData.efsData];
        efsData.sort((item1, item2) => {
            return item1.cap - item2.cap;
        });

        const max = efsData[efsData.length - 1].cap;
        if(max <= 0) {
            return {
                enabled: false,
            };
        }

        // --------
        const activeCartItems = getSelectedCartItems(cartData);
        for (let i = 0; i < activeCartItems.length; i++) {
            const cartItem = activeCartItems[i];

            if (cartItem.skuDepartmentID !== ProductDepartmentEnum.Bundles &&
                cartItem.skuDepartmentID !== ProductDepartmentEnum.Plugins &&
                cartItem.skuDepartmentID !== ProductDepartmentEnum.Software &&
                cartItem.skuDepartmentID !== ProductDepartmentEnum.Courses &&
                cartItem.skuDepartmentID !== ProductDepartmentEnum.CoursesWithSoftware) {

                // If other products in cart ---> don't show the progress bar
                return {
                    enabled: false,
                };
            }
        }
        // --------

        const subtotalWithoutDiscount = Math.max(0, cartData.priceData.subTotalPrice - cartData.priceData.totalDiscountPrice);

        const milestones: IEfsMilestone[] = [];

        const currentPricePercent = round2DecimalPlaces(Math.min(subtotalWithoutDiscount * 100 / max, 100));

        const checkedItem: IEfsCheckedItem = {
            percent: currentPricePercent,
            missingAmount: round2DecimalPlaces(Math.abs(efsData[0].cap - subtotalWithoutDiscount)),
            receiveCount: efsData[0].numberOfFreePlugins,
        };

        for(let i=0; i<efsData.length; i++) {

            const efsDataItem = efsData[i];
            const milestonePercent = round2DecimalPlaces(efsDataItem.cap * 100 / max);

            let isChecked = false;
            if(efsDataItem.cap <= subtotalWithoutDiscount) {

                isChecked = true;

                let receiveCount = 0;
                let missingAmount = 0;
                if(i + 1 < efsData.length) {
                    receiveCount = efsData[i + 1].numberOfFreePlugins;
                    missingAmount = round2DecimalPlaces(Math.abs(efsData[i + 1].cap - subtotalWithoutDiscount));
                }

                checkedItem.missingAmount = missingAmount;
                checkedItem.receiveCount = receiveCount;
            }

            milestones.push({
                amount: efsDataItem.cap,
                displayAmount: getDisplayAmount(efsDataItem.cap),
                percent: milestonePercent,
                isChecked,
            });
        }

        return {
            enabled: true,
            milestones,
            min: 0,
            max,
            checkedItem,
        }
    }
    catch (ex) {
        console.error(ex);
        return {
            enabled: false,
        };
    }
};

export const isTotalTiersEligible = (cartData: ICartData) => {
    if(!cartData.efsData || cartData.efsData.length <= 0) return false;

    const efsData = [...cartData.efsData];
    efsData.sort((item1, item2) => {
        return item1.cap - item2.cap;
    });

    const cap = Number(efsData[0].cap) || 0;
    const subtotalWithoutDiscount = Math.max(0, cartData.priceData.subTotalPrice - cartData.priceData.totalDiscountPrice);

    if(subtotalWithoutDiscount < cap) return false;

    const activeCartItems = getSelectedCartItems(cartData);

    for (let i = 0; i < activeCartItems.length; i++) {
        const cartItem = activeCartItems[i];

        if (cartItem.skuDepartmentID !== ProductDepartmentEnum.Bundles &&
            cartItem.skuDepartmentID !== ProductDepartmentEnum.Plugins &&
            cartItem.skuDepartmentID !== ProductDepartmentEnum.Software &&
            cartItem.skuDepartmentID !== ProductDepartmentEnum.Courses &&
            cartItem.skuDepartmentID !== ProductDepartmentEnum.CoursesWithSoftware) {
            return false;
        }
    }

    return true;
};

export const isFreePluginsBoxWithMilestoneVisible = (
    cartData: ICartData | null | undefined,
    _referrer: ConfirmationMsgVisibleReferer,
    cartStep = 1,
) : IConfirmationMsgVisibleResult => {

    if(!cartData) {
        return {
            addMoreEnabled: false,
            progressBarEnabled: false,
        }
    }

    const campaignID = Number(cartData.efsCampaignTypeID) || 0;
    if(campaignID !== FreePluginEnum.TotalTiers) {
        return {
            addMoreEnabled: false,
            progressBarEnabled: false,
        }
    }

    const data = initFreePluginProgressBarWithMilestones(cartData);

    const progressBarEnabled = cartStep === 3 ? false : (data?.enabled ?? false);

    // the campaign data should exist and be non-empty
    if(!isCampaignDataValid(cartData)) {
        return {
            addMoreEnabled: false,
            progressBarEnabled,
        }
    }

    const missingAmount = data?.checkedItem?.missingAmount ?? 0;

    return {
        addMoreEnabled: isTotalTiersEligible(cartData),
        progressBarEnabled,
        showTCLinkSeparately: missingAmount <= 0,
    };
};